// React-Screen CCancelBonMode ("Stornogrund") - Strangler Phase 5, Klasse B.
// Runbook: tools/REACT-STRANGLER.md.
// Ersetzt die ESM-Huelle cancelbonmode.js (aus
// lib/scripts/pos/components/CMP_CCancelBonMode.js).
// Legacy-DOM-Fixture: tests/react-fixtures/CCancelBonMode.html, gezogen aus
// tests/visual-baseline/14-overlay-bonstorno.html.
//
// Die Stornogruende stehen in einem geblaetterten Knopf-Raster. Das war bis
// 23.8.2026 ein echtes Legacy-Widget (CPagedPushPanel per
// createChildrenFromXML); seit dem Entschalen rendert React es selbst ueber
// PagedPushPanel aus react/widgets.jsx - Seitenrechnung und Blaetterknoepfe
// woertlich nach CPagedPushPanel.arrayFill. Die Gruende sind seitdem reine
// DATEN (Text + parameter_0..2), keine CPushButton-Objekte mehr.
//
// Zweite Neuerung: spec.prepare gibt hier ein jQuery-Deferred zurueck, weil
// die Stornogruende per SQL kommen (SELECT ... FROM CANRS) - genau wie das
// Legacy-prepare(), das ebenfalls ein Deferred lieferte.
import { defineReactScreen } from '../bridge.js';
import { CPushButton } from '../../components/pushbutton.js';
import { PagedPushPanel, verdrahtePagedPushPanel } from '../widgets.jsx';

// Legacy-CCaption: innerHTML = value UND class = "CCaption <addclass> <value>".
function Caption({ bridge, path, addclass, value, style }) {
  const cls = 'CCaption' + (addclass ? ' ' + addclass : '') + ' ' + value;
  return <p {...bridge.el(path, 'CCaption')} className={cls} style={style}
            dangerouslySetInnerHTML={{ __html: value }} />;
}

function CancelBonModeScreen({ bridge }) {
  const t = bridge.t;
  // Legacy: okButton bleibt ein CPushButton ohne Handler, aber unsichtbar.
  const okButton = bridge.widget('7', 'CPushButton', {
    events: { onMousedown: true, onMouseout: true, onMouseup: true },
    proto: CPushButton.prototype,
  });
  // Legacy: backButton.properties.events.onMousedown = this.finish
  const backButton = bridge.widget('8', 'CPushButton', {
    events: { onMousedown: () => bridge.doFinish(false), onMouseout: true, onMouseup: true },
    attributes: { onMousedown_parameter_0: bridge.id },
    proto: CPushButton.prototype,
  });

  // "Aus der Bonliste heraus" verschiebt das Overlay und nimmt den Rahmen -
  // Legacy setzt dafuer zusaetzliche Styles (Reihenfolge wie dort).
  const ausListe = !!bridge.bonFromList;
  const wholeOverlayStyle = ausListe
    ? { position: 'absolute', top: '-12.5%', left: '-60.06%', visibility: 'visible' }
    : { position: 'absolute', visibility: 'visible' };
  const overlayStyle = ausListe ? { position: 'absolute', border: '0px' } : { position: 'absolute' };
  const titleBackStyle = ausListe ? { position: 'absolute', border: '0px' } : { position: 'absolute' };

  return (
    <>
      {/* wholeOverlay */}
      <div {...bridge.el('0', 'CFrame')} className="CFrame wholeoverlay" style={wholeOverlayStyle}></div>
      {/* overlay */}
      <div {...bridge.el('1', 'CFrame')} className="CFrame overlay" style={overlayStyle}></div>
      {/* titleBack */}
      <div {...bridge.el('2', 'CFrame')} className="CFrame titleback" style={titleBackStyle}></div>
      {/* titleText */}
      <Caption bridge={bridge} path="3" addclass="titletext" value={t('LABELS', 'CANCEL_REASON_MODE')} />
      {/* contentText */}
      <Caption bridge={bridge} path="4" addclass="contenttext"
               value={t('LABELS', 'CANCEL_REASON_MODE_TEXT')} style={{ fontSize: '1.5vh' }} />
      {/* priceText */}
      <Caption bridge={bridge} path="5" addclass="pricetext" value=" " />
      {/* unitsText */}
      <Caption bridge={bridge} path="6" addclass="unitstext" value=" " />
      {/* okButton */}
      <div {...okButton} className="CPushButton okbutton" style={{ visibility: 'hidden' }}>
        <Caption bridge={bridge} path="7:0" addclass="" value=" " />
      </div>
      {/* backButton */}
      <div {...backButton} className="CPushButton backbutton">
        <p {...bridge.el('8:0', 'CCaption')} className="CCaption undefined"></p>
      </div>
      {/* selectPanel: die Stornogruende. Seit 23.8.2026 auch das ein
          React-Knoten - Raster und Blaetterlogik in PagedPushPanel, Spalten
          auf 2 (wie es das Legacy-makePagedPanel gesetzt hat), Zeilen 3 aus
          LYT_bonOverlayPagedPanel.xml. */}
      <PagedPushPanel bridge={bridge} pfad="9" zeilen={3} spalten={2}
                      knoepfe={bridge.cancelButtons || []} />
    </>
  );
}

export const CCancelBonMode = defineReactScreen({
  className: 'CCancelBonMode',
  addclass: 'CCancelBonMode CashWindow',
  htmlType: 'div',
  Screen: CancelBonModeScreen,
  // Legacy-prepare() ohne den XML-Teil: Wurzel-Styles und die Stornogruende
  // aus der DB. Gibt ein Deferred zurueck (SQL).
  prepare(bridge) {
    const defret = jQuery.Deferred();

    if (bridge.bonFromList) {
      bridge.properties.styles.top = '12.5%';
      bridge.properties.styles.left = '60.06%';
    }

    // SQL-Text ZEICHENGENAU wie im Legacy (Master/Slave-Routing).
    const sql = 'SELECT CANRS_ID, Reason FROM CANRS';
    $.when(POS.SQLiteCon.execute(sql)).then(function (rows) {
      const btns = new Array();

      for (var i = 0; i < rows.length; i++) {
        var reasonId = rows[i].CANRS_ID;
        var reason = rows[i].Reason;
        reason = umlauToEntity(reason);

        // Seit 23.8.2026 reine DATEN statt CPushButton-Objekten: das
        // Knopf-Raster rendert PagedPushPanel (react/widgets.jsx).
        // Handler-Signatur bleibt die Legacy-Signatur (component, parameters).
        btns[i] = {
          text: reason,
          handler: (component, parameters) => bridge.setReason(component, parameters),
          param0: bridge.id,
          param1: reasonId,
          param2: reason,
        };
      }

      bridge.cancelButtons = btns;

      // Legacy setzt die Sichtbarkeit erst hier, nach dem SQL.
      bridge.properties.styles.visibility = 'visible';

      defret.resolve();
    });

    return defret.promise();
  },
  // Nach dem Mount nur noch VERDRAHTUNG: Namen wie in der XML, und die
  // Seiten des Knopf-Rasters verketten (nextPage/prevPage hangeln sich
  // daran entlang).
  mountLegacy(bridge) {
    bridge.nameComponent('9', 'selectPanel');
    verdrahtePagedPushPanel(bridge, '9');
  },
  methods: {
    // Klick auf einen Stornogrund.
    setReason(bridge, component, parameters) {
      const reasonId = parameters[1];
      const reason = parameters[2];
      const defret = $.Deferred();

      if (bridge.bonFromList) {
        $.when(bridge.Main.BonManager.cancelBon(reasonId, reason, bridge.bonFromList)).done(function () {
          bridge.doFinish();
          $.when(bridge.BonListComp.buildNewList(bridge.BonListComp, '')).done(function () {
            defret.resolve();
          });
        });
        return;
      }

      const r = bridge.Main.BonManager.cancelBon(reasonId, reason);

      $.when(r).done(function () {
        bridge.Main.vacuumInfoValue(bridge.Main);

        if (typeof bridge.Main.cmpPay != 'undefined') {
          bridge.Main.cmpPay.restoreMainPos(bridge.Main.cmpPay);
          bridge.Main.cmpPay.removeMe();
        }

        bridge.doFinish(true);
        defret.resolve();
      });

      return defret.promise();
    },
    doFinish(bridge, message) {
      log.add(4, 'Info', 'Cancel bon mode out', '');

      bridge.removeMe();
    },
  },
});
